import React, { useState } from 'react';
import { useTranslation } from '../utils/translations';
import { TrendingUp } from 'lucide-react';

interface EndOfLessonSurveyProps {
  initialUnderstanding: number;
  onSubmit: (finalPercentage: number) => void;
  nativeLanguage?: string;
}

export function EndOfLessonSurvey({ initialUnderstanding, onSubmit, nativeLanguage }: EndOfLessonSurveyProps) {
  const [understanding, setUnderstanding] = useState(initialUnderstanding);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t, isRTL } = useTranslation(nativeLanguage);

  const handleSubmit = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onSubmit(understanding);
    } catch (error) {
      console.error('Error submitting survey:', error);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
            <TrendingUp className="w-8 h-8 text-blue-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {t('Lesson Complete!')}
          </h2>
          <p className="text-gray-600">
            {t('How much do you understand now?')}
          </p>
        </div>

        <div className="bg-blue-50 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">{t('Initial understanding')}</span>
            <span className="text-2xl font-bold text-blue-600">{initialUnderstanding}%</span>
          </div>
        </div>

        <div className="mb-8">
          <p className="text-sm font-medium text-gray-700 mb-4 text-center">
            {t('Current understanding level')}
          </p>

          <input
            type="range"
            min="0"
            max="100"
            value={understanding}
            onChange={(e) => setUnderstanding(Number(e.target.value))}
            className={`w-full understanding-slider understanding-slider-end mb-6 ${isRTL ? 'understanding-slider-rtl' : ''}`}
          />

          <div className="flex justify-between text-sm text-gray-500 mb-4">
            <span>{t("Don't understand")}</span>
            <span>{t('Fully understand')}</span>
          </div>

          <div className="text-center">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-green-400 to-green-600 shadow-lg">
              <span className="text-4xl font-bold text-white">{understanding}%</span>
            </div>
          </div>
        </div>

        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className={`w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors shadow-md hover:shadow-lg flex items-center justify-center ${
            isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
          }`}
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              {t('Submitting...')}
            </>
          ) : (
            t('Submit')
          )}
        </button>
      </div>
    </div>
  );
}
